/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { getSelfUsage } from "@/api/self";
import { Sparkline } from "@/components/common/Sparkline";
import { formatters } from "@/utils/timeSettings";
import { formatBytes } from "@/utils/formatBytes";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";

function uptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** How much the Nyrvex service itself costs the host it runs on. */
export const SelfWidget: React.FC = () => {
  const { data, isError } = useQuery({
    queryKey: ["self-usage"],
    queryFn: getSelfUsage,
    refetchInterval: 5000,
    retry: false,
  });

  const samples = data?.samples ?? [];
  const cpu = data?.cpuPercent ?? 0;
  const rss = data?.rssBytes ?? data?.sysBytes ?? 0;

  return (
    <section className="gh-w">
      <div className="gh-w-head">
        <span className="gh-label">This service</span>
        <span className="gh-sub">{data ? `Nyrvex ${data.version.match(/^v?\d+\.\d+\.\d+/)?.[0] ?? data.version}` : ""}</span>
      </div>

      {isError || !data ? (
        <div className="gh-empty">{isError ? "usage unavailable" : "loading…"}</div>
      ) : (
        <>
          <div className="gh-net-now">
            <div className="gh-net-fig gh-net-fig--rx">
              <span className="gh-big"><AnimatedNumber value={cpu} mountFrom={null} format={(n) => `${n.toFixed(1)}%`} /></span>
              <span>CPU</span>
            </div>
            <div className="gh-net-fig">
              <span className="gh-big"><AnimatedNumber value={rss} mountFrom={null} format={(n) => formatBytes(n)} /></span>
              <span>MEMORY</span>
            </div>
          </div>

          <div className="gh-spark gh-net-chart">
            <div className="gh-chart-head">
              <span>CPU · last {Math.max(1, Math.round((samples.length * 5) / 60))} min</span>
              <span>peak {Math.max(0, ...samples.map((s) => s.cpu)).toFixed(1)}%</span>
            </div>
            <Sparkline values={samples.map((s) => s.cpu)} color="#5fa37a" height={34} smooth={4} name="cpu" format={(v) => `${v.toFixed(1)}%`} heads={samples.map((s) => formatters.time(new Date(s.t * (s.t < 1e12 ? 1000 : 1))))} />
            <div className="gh-chart-head" style={{ marginTop: 8 }}>
              <span>memory</span>
              <span>peak {formatBytes(Math.max(0, ...samples.map((s) => s.rss)))}</span>
            </div>
            <Sparkline values={samples.map((s) => s.rss)} color="#c98a5b" height={34} name="memory" format={(v) => formatBytes(v)} heads={samples.map((s) => formatters.time(new Date(s.t * (s.t < 1e12 ? 1000 : 1))))} />
          </div>

          <div className="gh-net-meta">
            <span>
              up {uptime(data.uptimeSeconds)} · {data.goroutines} goroutines
              {data.threads ? ` · ${data.threads} threads` : ""}
            </span>
            <span>
              heap {formatBytes(data.heapBytes)}
              {data.dbBytes ? ` · db ${formatBytes(data.dbBytes)}` : ""}
              {data.openFiles ? ` · ${data.openFiles} files` : ""}
            </span>
          </div>
          {data.diskTotal ? (
            <div className="gh-net-meta">
              <span className={(data.diskPercent ?? 0) >= 90 ? "text-[var(--gh-crit)]" : undefined}>
                {`disk ${Math.round(data.diskPercent ?? 0)}% used · ${formatBytes(data.diskFree ?? 0)} free`}
              </span>
              {data.backupCount ? (
                <span>{`backups ${data.backupCount} · last ${new Date(data.backupAt ?? 0).toLocaleDateString()}`}</span>
              ) : (
                <span>no backups yet</span>
              )}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
